import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Info, Wallet, ShieldCheck, Clock } from 'lucide-react';
import lmsApi from '../services/api';
import { toast } from 'sonner';
import { isWithinTimeWindow } from '../utils/timeUtils';
import { DIGIO_ENVIRONMENT, PAYMENT_GATEWAY_MODE } from '@/config';
import { initiatePayment, type JuspayInitiateResponse } from '../services/juspayApi';
import JuspayPaymentSheet from './JuspayPaymentSheet';

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    loan: any;
    onPaymentSuccess?: () => void;
    initialAction?: 'PAY' | 'CANCEL' | 'FORECLOSE';
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, loan, onPaymentSuccess, initialAction }) => {
    const [actionInfo, setActionInfo] = useState<any>(null);
    const [paymentType, setPaymentType] = useState<'FULL' | 'PARTIAL'>('FULL');
    const [customAmount, setCustomAmount] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Juspay state — only used when PAYMENT_GATEWAY_MODE !== 'sim'
    const [juspayOrder, setJuspayOrder] = useState<JuspayInitiateResponse | null>(null);

    const isDigioWindowOpen = isWithinTimeWindow(6, 0, 20, 0); // Requirement 4: 6 AM – 8 PM IST
    // On UAT (sandbox) the time-window restriction is bypassed so testers can verify the flow
    // at any hour. In production this must remain enforced.
    const isUatBypass = DIGIO_ENVIRONMENT === 'sandbox';

    const fetchActionInfo = async () => {
        if (!loan || !isOpen) return;
        setLoading(true);
        try {
            // loan.loan_id is used by SimState objects from the simulation dashboard.
            // loan.loan_application_number / intellend_loan_id / id are used by real loan objects.
            const loanId = loan.loan_id || loan.loan_application_number || loan.intellend_loan_id || loan.id;
            const response = await lmsApi.post('/sim/action-info/', {
                loan_id: loanId,
                action: initialAction || 'PAY'
            });
            setActionInfo(response.data);
            setCustomAmount(response.data.amount.toString());
        } catch (err) {
            toast.error('Failed to get payment details');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && loan) {
            fetchActionInfo();
        } else {
            setActionInfo(null);
            setPaymentType('FULL');
        }
    }, [isOpen, loan]);

    const handleLaunchGateway = async () => {
        if (!loan || !actionInfo) return;

        const finalAmount = paymentType === 'FULL' ? actionInfo.amount : parseFloat(customAmount);

        if (isNaN(finalAmount) || finalAmount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        if (finalAmount > actionInfo.amount) {
            toast.error(`Amount cannot exceed total due (₹${actionInfo.amount.toFixed(2)})`);
            return;
        }

        setSubmitting(true);
        try {
            const loanId = loan.loan_id || loan.loan_application_number || loan.intellend_loan_id || loan.id;
            const result = await initiatePayment({
                loan_id: loanId,
                action: (initialAction || 'PAY') as 'PAY' | 'CANCEL' | 'FORECLOSE',
                amount: finalAmount,
            });

            if (result.mode === 'sim') {
                // Simulation path — open the PASS/FAIL gateway in a new window (unchanged)
                window.open(result.gateway_url, '_blank', 'width=600,height=700');
                onClose();
                toast.info('Payment gateway opened. Complete the payment there.');
                if (onPaymentSuccess) onPaymentSuccess();
            } else {
                // Juspay path — show the UPI Intent sheet inside this modal
                setJuspayOrder(result as JuspayInitiateResponse);
            }
        } catch (err: any) {
            const backendMsg = err?.response?.data?.error || err?.response?.data?.detail || err?.message;
            toast.error(backendMsg ? `Failed to launch gateway: ${backendMsg}` : 'Failed to launch gateway');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    // ---- Juspay UPI Intent sheet (replaces the confirm/pay UI when Juspay mode is active) ----
    if (juspayOrder) {
        return (
            <Dialog open={isOpen} onOpenChange={() => {
                setJuspayOrder(null);
                onClose();
            }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <div className="flex justify-center mb-2">
                            <div className="bg-primary/10 p-3 rounded-full">
                                <ShieldCheck className="h-8 w-8 text-primary" />
                            </div>
                        </div>
                        <DialogTitle className="text-xl font-bold text-center">
                            Secure UPI Payment
                        </DialogTitle>
                    </DialogHeader>
                    <JuspayPaymentSheet
                        orderId={juspayOrder.order_id}
                        customerId={juspayOrder.loan_id}
                        amount={juspayOrder.amount}
                        action={(initialAction || 'PAY') as 'PAY' | 'CANCEL' | 'FORECLOSE'}
                        onSuccess={() => {
                            setJuspayOrder(null);
                            onClose();
                            if (onPaymentSuccess) onPaymentSuccess();
                        }}
                        onCancel={() => {
                            setJuspayOrder(null);
                            onClose();
                        }}
                    />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <div className="flex justify-center mb-4">
                        <div className="bg-primary/10 p-3 rounded-full">
                            <ShieldCheck className="h-8 w-8 text-primary" />
                        </div>
                    </div>
                    <DialogTitle className="text-xl font-bold text-center">
                        {(initialAction === 'CANCEL') ? 'Confirm Cancel Loan' : (initialAction === 'FORECLOSE' ? 'Confirm Foreclosure' : 'Confirm Pay Total Due')}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Loading payment details...</div>
                ) : actionInfo && (
                    <div className="my-6 space-y-4">
                        {actionInfo.amount < 1 ? (
                            <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-3 text-blue-800 text-sm">
                                <Info className="h-5 w-5 shrink-0 text-blue-400" />
                                <p className="font-medium italic">
                                    No pending total due amount. If you have made any payments, they will reflect in EOD.
                                </p>
                            </div>
                        ) : (
                            <>
                                <RadioGroup value={paymentType} onValueChange={(v: any) => setPaymentType(v)} className="grid grid-cols-2 gap-4">
                                    <div>
                                        <RadioGroupItem value="FULL" id="full" className="peer sr-only" />
                                        <Label
                                            htmlFor="full"
                                            className="flex flex-col items-center justify-between rounded-xl border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer transition-all"
                                        >
                                            <Wallet className="mb-2 h-6 w-6 text-slate-400 group-data-[state=checked]:text-primary" />
                                            <span className="text-xs font-bold uppercase tracking-tight">
                                                {(initialAction === 'CANCEL') ? 'Full Repayment' : (initialAction === 'FORECLOSE' ? 'Full Foreclosure' : 'Full Total Due')}
                                            </span>
                                            <span className="text-[10px] text-slate-500 font-medium mt-1">₹{actionInfo.amount.toFixed(2)}</span>
                                        </Label>
                                    </div>
                                    <div>
                                        <RadioGroupItem value="PARTIAL" id="partial" className="peer sr-only" />
                                        <Label
                                            htmlFor="partial"
                                            className="flex flex-col items-center justify-between rounded-xl border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer transition-all"
                                        >
                                            <div className="h-6 w-6 mb-2 flex items-center justify-center font-black text-slate-400">₹</div>
                                            <span className="text-xs font-bold uppercase tracking-tight">Partial</span>
                                            <span className="text-[10px] text-rose-500 font-medium mt-1">Min ₹1.00</span>
                                        </Label>
                                    </div>
                                </RadioGroup>

                                {actionInfo.description && (
                                    <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex gap-3 text-slate-600 text-[10px] font-medium italic">
                                        <Info className="h-4 w-4 shrink-0 text-slate-400" />
                                        <p>{actionInfo.description}</p>
                                    </div>
                                )}

                                {paymentType === 'PARTIAL' && (
                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <Label htmlFor="amount" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Enter Amount</Label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-slate-300 pointer-events-none">₹</span>
                                            <Input
                                                id="amount"
                                                type="number"
                                                value={customAmount}
                                                onChange={(e) => setCustomAmount(e.target.value)}
                                                className="h-12 pl-8 font-bold text-lg border-2 focus-visible:ring-0 focus-visible:border-primary transition-all"
                                                placeholder="0.00"
                                                autoFocus
                                            />
                                        </div>
                                        <p className="text-[9px] text-slate-400 italic px-1">Maximum allowed: ₹{actionInfo.amount.toFixed(2)}</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {!isDigioWindowOpen && (
                    <div className={`flex items-start gap-2 rounded-lg p-3 text-xs border ${isUatBypass ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-slate-600 bg-slate-50 border-slate-200'}`}>
                        <Clock className={`h-4 w-4 shrink-0 mt-0.5 ${isUatBypass ? 'text-amber-500' : 'text-slate-400'}`} />
                        <span>
                            Outstanding payments can be made from <strong>6:00 AM to 8:00 PM IST</strong>. Past this window we will automatically adjust your next installment.
                            {isUatBypass && (
                                <span className="block mt-1 font-semibold text-amber-800">
                                    ⚠️ UAT bypass active — button is enabled for testing only. This will be blocked in production.
                                </span>
                            )}
                        </span>
                    </div>
                )}
                <DialogFooter className="grid grid-cols-2 gap-3 sm:space-x-0">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleLaunchGateway}
                        disabled={submitting || loading || !actionInfo || (actionInfo?.amount < 1) || (!isDigioWindowOpen && !isUatBypass)}
                        className="font-bold"
                        title={(!isDigioWindowOpen && !isUatBypass) ? 'Payments only allowed 6:00 AM – 8:00 PM IST' : undefined}
                    >
                        {actionInfo?.amount < 1 ? 'Up to Date' : 'Pay Now'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentModal;
